import React, { useState } from 'react';
import { FormikProps, setIn } from 'formik';
import { DatePicker, SalvDropdown, SalvNumberField, SalvTextField, Size } from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import dayjs, { Dayjs } from 'dayjs';
import {
  InternalApiAttributeType,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiPersonCategory,
  InternalApiSearchFilter,
  InternalApiSearchOperator,
} from '../api-internal';
import { getSearchAttributeOptions } from '../schema/helper';
import { getOperatorOptions, notPrefix } from './helper';
import { defaultSearchFilter, GeneralField } from './PersonSearch';
import { FormikErrors } from 'formik/dist/types';
import { PersonSearchFormValues } from './PersonSearchForm';
import { DATE_FORMAT, ZONED_DATE_TIME_FORMAT } from '../common/helper';
import { doesDateHaveTimeComponent } from '../common/datetime/FormattedDateTime';

type PersonSearchFormRowProps = {
  index: number;
  filter: InternalApiSearchFilter;
  category: InternalApiPersonCategory;
  setFieldValue: FormikProps<PersonSearchFormValues>['setFieldValue'];
  setValues: FormikProps<PersonSearchFormValues>['setValues'];
  errors: FormikErrors<InternalApiSearchFilter> | undefined;
  schemaFields: InternalApiEntitySchemaField[];
};

export const operatorsWithoutQuery = [InternalApiSearchOperator.EXISTS];

export const PersonSearchFormRow = ({
  index,
  filter,
  category,
  setFieldValue: propsSetFieldValue,
  setValues,
  schemaFields,
  errors = {},
}: PersonSearchFormRowProps) => {
  const setFieldValue = async (field: string, value?: string) =>
    await propsSetFieldValue(`filters.${index}.${field}`, value);
  const attributeOptions = (() => {
    const defaultOptions: DropdownOption[] = [
      {
        key: GeneralField.EXTERNAL_ID.toString(),
        label: 'Person ID',
        value: GeneralField.EXTERNAL_ID.toString(),
      },
      {
        key: GeneralField.TYPE.toString(),
        label: 'Person Type',
        value: GeneralField.TYPE.toString(),
      },
    ];

    const options = getSearchAttributeOptions(schemaFields);
    if (category === InternalApiPersonCategory.COUNTERPARTY) {
      return defaultOptions.concat(
        options.filter((option: DropdownOption) => {
          const schemaField = schemaFields.find((field) => field.id === option.value);
          return schemaField?.fieldName.startsWith('counterparty');
        })
      );
    } else {
      return defaultOptions.concat(options);
    }
  })();

  const schemaField =
    filter.attributeType === InternalApiAttributeType.SCHEMA_FIELD
      ? schemaFields.find((schemaField) => schemaField.id === filter.attributeIdentifier)!
      : undefined;

  const [showTime, setShowTime] = useState(() => {
    if (filter.query && schemaField?.type === InternalApiEntitySchemaFieldType.DATETIME) {
      return doesDateHaveTimeComponent(filter.query);
    } else {
      return false;
    }
  });

  const operatorOptions = getOperatorOptions({
    schemaFieldType: schemaField?.type,
  });

  const resetQueryAndOperatorIfNewAttributeIsIncompatible = async (
    newAttributeIdentifier: string,
    newAttributeType: InternalApiAttributeType
  ) => {
    const newSchemaField =
      newAttributeType === InternalApiAttributeType.SCHEMA_FIELD
        ? schemaFields?.find((schemaField) => schemaField.id === newAttributeIdentifier)
        : undefined;

    if (newSchemaField?.type !== schemaField?.type || newSchemaField?.type === InternalApiEntitySchemaFieldType.ENUM) {
      await setFieldValue('query', defaultSearchFilter.query);
      await setFieldValue('operator', defaultSearchFilter.operator);
    }
  };

  const resetQueryIfNewOperatorIsIncompatible = async (newOperator: InternalApiSearchOperator) => {
    if (operatorsWithoutQuery.includes(newOperator) && !operatorsWithoutQuery.includes(filter.operator)) {
      await setFieldValue('query', defaultSearchFilter.query);
    }
  };

  const fieldEnumOptions: DropdownOption[] | undefined =
    schemaField?.enumOptions?.map((option) => ({
      key: option,
      label: option,
      value: option,
    })) || undefined;

  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      <SalvDropdown
        size={Size.SMALL}
        clearable={false}
        ariaLabel={`Attribute for filter ${index + 1}`}
        options={attributeOptions}
        value={attributeOptions.find((option) => option.value === filter.attributeIdentifier)}
        onChange={async ([option]: DropdownOption[]) => {
          const attributeType = Object.values(GeneralField).includes(option.value as GeneralField)
            ? InternalApiAttributeType.GENERAL
            : InternalApiAttributeType.SCHEMA_FIELD;

          await setFieldValue('attributeIdentifier', option.value);
          await setFieldValue('attributeType', attributeType);
          await resetQueryAndOperatorIfNewAttributeIsIncompatible(option.value, filter.attributeType);
        }}
        isInvalid={!!errors.attributeIdentifier}
      />

      <SalvDropdown
        size={Size.SMALL}
        clearable={false}
        ariaLabel={`Operator for filter ${index + 1}`}
        placeholder='Select...'
        value={operatorOptions.find(
          (option) => option.value === filter.operator && option.key.startsWith(notPrefix) === filter.not
        )}
        options={operatorOptions}
        onChange={async ([option]: DropdownOption[]) => {
          await setValues((prevValues) =>
            setIn(prevValues, `filters.${index}`, {
              ...filter,
              operator: option.value,
              not: option.key.startsWith(notPrefix),
            })
          );

          await resetQueryIfNewOperatorIsIncompatible(option.value as InternalApiSearchOperator);
        }}
        isInvalid={!!errors.operator}
      />

      {operatorsWithoutQuery.includes(filter.operator) ? null : schemaField?.type ===
        InternalApiEntitySchemaFieldType.DATETIME ? (
        <DatePicker
          isClearable={true}
          size='small'
          value={filter.query && dayjs(filter.query).isValid() ? dayjs(filter.query) : null}
          showDefaultOptions={false}
          onChange={async (val: Dayjs | null) => {
            await setFieldValue('query', val ? val.format(showTime ? ZONED_DATE_TIME_FORMAT : DATE_FORMAT) : undefined);
          }}
          isInvalid={!!errors.query}
          showTime={showTime}
          onShowTimeChange={(newShowTime) => setShowTime(newShowTime)}
          showTimeOptIn
        />
      ) : schemaField?.type === InternalApiEntitySchemaFieldType.NUMBER ? (
        <SalvNumberField
          size={Size.SMALL}
          ariaLabel={`Query for filter ${index + 1}`}
          value={filter.query ? Number(filter.query) : undefined}
          onChange={async (value) => {
            await setFieldValue('query', value.toString());
          }}
          isInvalid={!!errors.query}
        />
      ) : schemaField?.type === InternalApiEntitySchemaFieldType.ENUM ? (
        <SalvDropdown
          size={Size.SMALL}
          clearable={false}
          ariaLabel={`Query for filter ${index + 1}`}
          options={fieldEnumOptions!}
          value={fieldEnumOptions!.find((option) => option.value === filter.query)!}
          onChange={async ([option]) => {
            await setFieldValue('query', option.value);
          }}
          isInvalid={!!errors.query}
        />
      ) : (
        <SalvTextField
          size={Size.SMALL}
          ariaLabel={`Query for filter ${index + 1}`}
          value={filter.query || ''}
          onChange={async (value) => {
            await setFieldValue('query', value);
          }}
          isInvalid={!!errors.query}
        />
      )}
    </div>
  );
};

export default PersonSearchFormRow;
